import React from "react";
import { useRecoilState } from "recoil";
import { Grid, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  NewReqInsurancePadding,
  NewRequisitionTextField,
  NewRequisitionTextFieldLabel,
  PatientBillingBox,
  PatientBillingDateWrapper,
  PolicyOwnerRelationshipText,
  RadioFormControl,
  StyledFormLabel,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import {
  getRelationshipToInsured,
  getInsuredParameter,
  checkDateValidationError,
} from "./helpers";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";

const NewReqBillToInsuranceEntry: React.FC = () => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const requisitionForm = { ...newRequisition.requisitionForm };
  const isDisable = requisitionForm.insuranceSheetAttached
    ? { opacity: 0.35 }
    : { opacity: 1 };
  const showEntryErrors =
    newRequisition.isError && requisitionForm.billToInsurance;

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewRequisition({
      ...newRequisition,
      requisitionForm: {
        ...newRequisition.requisitionForm,
        [e.target.name]: e.target.value,
      },
    });
  };

  return (
    <>
      <Grid item xs={3}>
        <FormControlLabel
          value="Entry"
          control={<Radio />}
          label={GetTypographyContent({
            content: "Enter details below",
            size: "medium",
            textColor: "greyMedium",
          })}
        />
      </Grid>
      {requisitionForm.billToInsuranceType === "Entry" && (
        <NewReqInsurancePadding>
          <Grid container spacing={3} sx={isDisable}>
            <Grid item xs={12} sm={4} md={4}>
              <NewRequisitionTextField
                fullWidth
                focused
                placeholder="Insurance Company Name"
                error={
                  showEntryErrors && !requisitionForm.insuranceCompanyName
                    ? "Error"
                    : ""
                }
                InputLabelProps={{ className: "insurance-label" }}
                label={
                  <NewRequisitionTextFieldLabel>
                    Insurance Company Name*
                    {showEntryErrors &&
                      !requisitionForm.insuranceCompanyName && (
                        <ExclamationIcon />
                      )}
                  </NewRequisitionTextFieldLabel>
                }
                variant="standard"
                disabled={
                  requisitionForm.insuranceSheetAttached ||
                  !requisitionForm.billToInsurance
                }
                name="insuranceCompanyName"
                value={requisitionForm.insuranceCompanyName}
                onChange={onChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <NewRequisitionTextField
                fullWidth
                focused
                placeholder="Group ID #"
                error={
                  showEntryErrors && !requisitionForm.groupId ? "Error" : ""
                }
                InputLabelProps={{ className: "insurance-label" }}
                label={
                  <NewRequisitionTextFieldLabel>
                    {" "}
                    Group ID #*{" "}
                    {showEntryErrors && !requisitionForm.groupId && (
                      <ExclamationIcon />
                    )}
                  </NewRequisitionTextFieldLabel>
                }
                variant="standard"
                disabled={
                  requisitionForm.insuranceSheetAttached ||
                  !requisitionForm.billToInsurance
                }
                value={requisitionForm.groupId}
                name="groupId"
                onChange={onChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <NewRequisitionTextField
                fullWidth
                focused
                placeholder="Member ID #"
                error={
                  showEntryErrors && !requisitionForm.memberId ? "Error" : ""
                }
                label={
                  <NewRequisitionTextFieldLabel>
                    Member ID #*
                    {showEntryErrors && !requisitionForm.memberId && (
                      <ExclamationIcon />
                    )}
                  </NewRequisitionTextFieldLabel>
                }
                variant="standard"
                disabled={
                  requisitionForm.insuranceSheetAttached ||
                  !requisitionForm.billToInsurance
                }
                value={requisitionForm.memberId}
                name="memberId"
                onChange={onChange}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledFormLabel
                style={{ lineHeight: 2.5, textTransform: "none" }}
                error={
                  newRequisition.isError &&
                  requisitionForm.billToInsurance &&
                  !requisitionForm.relationshipToPolicyOwnerDependent &&
                  !requisitionForm.relationshipToPolicyOwnerSelf &&
                  !requisitionForm.relationshipToPolicyOwnerOther
                }
              >
                <PolicyOwnerRelationshipText>
                  {GetTypographyContent({
                    content: "Relationship to Policy Owner*",
                    size: "small",
                    textColor: "blueDark",
                  })}
                </PolicyOwnerRelationshipText>
              </StyledFormLabel>
              <PatientBillingBox>
                <RadioFormControl
                  $hideBorder
                  error={
                    newRequisition.isError &&
                    requisitionForm.billToInsurance &&
                    !requisitionForm.relationshipToPolicyOwnerDependent &&
                    !requisitionForm.relationshipToPolicyOwnerSelf &&
                    !requisitionForm.relationshipToPolicyOwnerOther
                  }
                >
                  <RadioGroup
                    value={getRelationshipToInsured(
                      requisitionForm.relationshipToPolicyOwnerSelf,
                      requisitionForm.relationshipToPolicyOwnerDependent,
                      requisitionForm.relationshipToPolicyOwnerOther,
                    )}
                    onChange={(e) =>
                      setNewRequisition({
                        ...newRequisition,
                        requisitionForm: {
                          ...newRequisition.requisitionForm,
                          relationshipToPolicyOwnerSelf: false,
                          relationshipToPolicyOwnerDependent: false,
                          relationshipToPolicyOwnerOther: false,
                          [getInsuredParameter(
                            e.target.value as "Self" | "Dependent" | "Other",
                          )]: true,
                        },
                      })
                    }
                    row
                  >
                    <Grid container spacing={2} />
                    <Grid item xs={3}>
                      <FormControlLabel
                        value="Self"
                        control={<Radio />}
                        label={GetTypographyContent({
                          content: "Self",
                          size: "medium",
                          textColor: "greyMedium",
                        })}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FormControlLabel
                        value="Dependent"
                        control={<Radio />}
                        label={GetTypographyContent({
                          content: "Dependent",
                          size: "medium",
                          textColor: "greyMedium",
                        })}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <FormControlLabel
                        value="Other"
                        control={<Radio />}
                        label={GetTypographyContent({
                          content: "Other",
                          size: "medium",
                          textColor: "greyMedium",
                        })}
                      />
                    </Grid>
                  </RadioGroup>
                </RadioFormControl>
              </PatientBillingBox>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <NewRequisitionTextField
                fullWidth
                focused
                placeholder="Policy Owner First Name"
                error={
                  showEntryErrors && !requisitionForm.policyOwnerFirstName
                    ? "Error"
                    : ""
                }
                label={
                  <NewRequisitionTextFieldLabel>
                    Policy Owner First Name*
                    {showEntryErrors &&
                      !requisitionForm.policyOwnerFirstName && (
                        <ExclamationIcon />
                      )}
                  </NewRequisitionTextFieldLabel>
                }
                variant="standard"
                disabled={
                  requisitionForm.insuranceSheetAttached ||
                  !requisitionForm.billToInsurance
                }
                value={requisitionForm.policyOwnerFirstName}
                name="policyOwnerFirstName"
                onChange={onChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <NewRequisitionTextField
                fullWidth
                focused
                placeholder="Policy Owner Last Name"
                error={
                  showEntryErrors && !requisitionForm.policyOwnerLastName
                    ? "Error"
                    : ""
                }
                label={
                  <NewRequisitionTextFieldLabel>
                    Policy Owner Last Name*
                    {showEntryErrors &&
                      !requisitionForm.policyOwnerLastName && (
                        <ExclamationIcon />
                      )}
                  </NewRequisitionTextFieldLabel>
                }
                variant="standard"
                disabled={
                  requisitionForm.insuranceSheetAttached ||
                  !requisitionForm.billToInsurance
                }
                value={requisitionForm.policyOwnerLastName}
                name="policyOwnerLastName"
                onChange={onChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <PatientBillingDateWrapper>
                <NewRequisitionTextField
                  fullWidth
                  focused
                  type={
                    requisitionForm.insuranceSheetAttached ||
                    !requisitionForm.billToInsurance
                      ? "text"
                      : "date"
                  }
                  $noDateValue={!requisitionForm.policyOwnerDob}
                  placeholder="MM-DD-YYYY"
                  error={
                    showEntryErrors &&
                    checkDateValidationError("DOBPO", requisitionForm)
                      ? checkDateValidationError("DOBPO", requisitionForm)
                      : ""
                  }
                  label={
                    <NewRequisitionTextFieldLabel>
                      Policy Owner DOB*
                      {showEntryErrors &&
                        checkDateValidationError("DOBPO", requisitionForm) && (
                          <ExclamationIcon />
                        )}
                    </NewRequisitionTextFieldLabel>
                  }
                  variant="standard"
                  $showHelpertext={Boolean(
                    showEntryErrors &&
                    checkDateValidationError("DOBPO", requisitionForm),
                  )}
                  disabled={
                    requisitionForm.insuranceSheetAttached ||
                    !requisitionForm.billToInsurance
                  }
                  value={requisitionForm.policyOwnerDob}
                  name="policyOwnerDob"
                  onChange={onChange}
                />
              </PatientBillingDateWrapper>
            </Grid>
          </Grid>
        </NewReqInsurancePadding>
      )}
    </>
  );
};
export default NewReqBillToInsuranceEntry;
